import React, {
  useState, useEffect, cloneElement, useCallback, useMemo,
} from 'react';
import { debounce, isNil } from 'lodash';
import MultiSelect from './BaseMultiSelect';
import Button from 'shared/buttons';
import { Icon } from '@progress/kendo-react-common';
import { requestStatuses } from 'shared/../constants/store';
import { ENTER_KEY_CODE } from 'src/shared/constants/keyCodes';

/**
 * An async multi select component. Loads and displays data when User enters a search string.
 *
 * @typedef {React.ComponentProps<MultiSelect>} BaseMultiSelectProps
 *
 * @typedef {Object} AdditionalAsyncMultiSelectProps
 * @property {Function} getData A callback to load data by a search string.
 * @property {boolean} [loadInitialData={false}] Indicates if data need to be loaded the data for the initial values from the api. False by default.
 * @property {boolean} [reloadOnOpen={false}] Indicates if data needs to be loaded when the dropdown is open. False by default.
 * @property {boolean} [fetchOnMount={false}] Indicates if data need to be loaded when the dropdown mounted. False by default.
 * @property {Function} [onQuickAdd] A callback when clicking Plus icon action button. The button is added if the callback is set.
 * @property {boolean} [forceEnableQuickAdd] Always enable Plus icon button which calls onQuickAdd callback.
 *
 * @param {BaseMultiSelectProps & AdditionalAsyncMultiSelectProps} props Component props.
 * @param {React.Ref<HTMLDivElement>} ref
 * @returns {React.ReactElement} Async multi select component.
 */
const AsyncMultiSelect = ({
  getData,
  onChange,
  dataItemKey = 'value',
  textField = 'description',
  reloadOnOpen = false,
  fetchOnMount = false,
  disabled,
  forceEnableQuickAdd,
  onQuickAdd,
  autoClose = false,
  onBlur,
  ...props
}) => {
  const [search, setSearch] = useState(null);

  const [data, setData] = useState([]);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState(requestStatuses.INIT);

  const reset = () => {
    setData([]);
    setError(null);
    setStatus(requestStatuses.INIT);
  };

  const listNoDataRender = (element) => {
    const getIcon = () => {
      if (status === requestStatuses.FAILED) {
        return 'error';
      }
      if (status === requestStatuses.PENDING) {
        return 'loading';
      }
      return 'info';
    };

    const getMessage = () => {
      if (status === requestStatuses.FAILED) {
        return error.message;
      }
      if (status === requestStatuses.PENDING) {
        return 'Loading...';
      }

      if (reloadOnOpen) {
        return 'No Items';
      }

      if (isNil(search) || search.trim().length === 0) {
        return 'Type to Search';
      }

      return 'No Items';
    };

    const noData = (
      <div>
        <div>
          <Icon name={getIcon()} />
        </div>
        <div>{getMessage()}</div>
      </div>
    );
    return cloneElement(element, { ...element.props }, noData);
  };

  const debounced = useCallback(
    debounce((search, allowEmpty = false) => {
      if (!allowEmpty && (isNil(search) || search.trim().length === 0)) {
        return null;
      }

      setStatus(requestStatuses.PENDING);
      return getData(search ? search.trim() : undefined)
        .then((data) => {
          setData(data ?? []);
          setStatus(requestStatuses.SUCCESS);
        })
        .catch((error) => {
          setError(error);
          setStatus(requestStatuses.FAILED);
        });
    }, 300),
    [],
  );

  // Initial data
  useEffect(() => {
    if (fetchOnMount) {
      debounced(null, true);
    }
  }, []);

  const onFilterChange = ({ filter, nativeEvent }) => {
    // Do not reset filter if an item is selected. Telerik tries to reset it automatically.
    if (!autoClose && (nativeEvent?.type === 'click' || nativeEvent?.keyCode === ENTER_KEY_CODE)) {
      return;
    }
    setSearch(filter?.value ?? null);
  };

  const handleChange = (e) => {
    if (autoClose) {
      onFilterChange({ filter: null });
    }

    if (onChange) {
      onChange(e);
    }
  };

  const handleBlur = (e) => {
    onFilterChange({ filter: null });

    if (onBlur) {
      onBlur(e);
    }
  };

  useEffect(() => {
    if ((isNil(search) || search.trim() === '') && data.length > 0) {
      if (reloadOnOpen) {
        debounced(null, true);
        return;
      }
      reset();
      return;
    }
    debounced(search, reloadOnOpen);
  }, [search]);

  const onOpen = () => {
    if (reloadOnOpen) {
      debounced('', true);
    }
  };

  const options = useMemo(
    () => data.filter((item) => (!isNil(item[textField]))),
    [data],
  );

  const multiSelect = (
    <MultiSelect
      data={options}
      loading={status === requestStatuses.PENDING}
      listNoDataRender={listNoDataRender}
      filter={search === '' ? null : search}
      onFilterChange={onFilterChange}
      onChange={handleChange}
      onOpen={onOpen}
      dataItemKey={dataItemKey}
      textField={textField}
      autoClose={autoClose}
      onBlur={handleBlur}
      {...props}
    />
  );

  if (onQuickAdd) {
    return (
      <div className='input-items-container'>
        {multiSelect}
        <Button
          icon='plus'
          className='quick-add-button'
          fillMode='flat'
          themeColor='tertiary'
          disabled={disabled && !forceEnableQuickAdd}
          onClick={onQuickAdd}
        />
      </div>
    );
  }

  return multiSelect;
};

export default AsyncMultiSelect;
